package com.tregz.tremenz.ui.media

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import com.tregz.design.motion.isReducedMotion
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import kotlin.math.PI
import kotlin.math.pow
import kotlin.math.sin

private val LABEL_SIZE = 56.dp

/** Beam radius as a share of the label height. */
private const val BEAM_RADIUS = 1.2f

/** Built once: the sweep recomposes every frame and the stops never change. */
private val SPOTLIGHT_COLORS = listOf(
    Color.White,
    Color.White.copy(alpha = 0.95f),
    Color.White.copy(alpha = 0.35f),
    Color.Transparent
)

/** The spotlight "loading" animation shown while a track is being prepared. */
@Composable
fun MediaOverlay(modifier: Modifier = Modifier) {
    // A still beam centered on the label stands in for the sweep when reduced motion is requested.
    val progress = if (isReducedMotion()) {
        0f
    } else {
        val transition = rememberInfiniteTransition(label = "SpotlightTransition")
        val sweep by transition.animateFloat(
            initialValue = 0f,
            targetValue = 1f,
            animationSpec = infiniteRepeatable(
                animation = tween(durationMillis = 1800, easing = LinearEasing),
                repeatMode = RepeatMode.Restart
            ),
            label = "SpotlightProgress"
        )
        sweep
    }

    val f = 0.6
    val t = progress.toDouble()
    val interpolated = 2.0.pow(-6.5 * t) * sin(2.0 * PI * (t - f / 4.0) / f) + 1.0
    // -1..1 across the label, scaled below by its measured width.
    val swing = sin((interpolated - 1.0) * PI).toFloat()

    val loading = labelText(LabelKey.STATUS_LOADING)
    // A display graphic sized to the player like an image: in dp so a large font scale cannot wrap it.
    val fontSize = with(LocalDensity.current) { LABEL_SIZE.toSp() }
    var labelSize by remember { mutableStateOf(IntSize.Zero) }
    val half = labelSize.width / 2f

    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center
    ) {
        // Base dark text
        Text(
            text = loading,
            fontSize = fontSize,
            fontWeight = FontWeight.Bold,
            color = Color(0xFF808080),
            maxLines = 1,
            softWrap = false,
            modifier = Modifier.onSizeChanged { labelSize = it }
        )

        // Spotlight beam sweeping across the letters with white cone
        Text(
            text = loading,
            fontSize = fontSize,
            fontWeight = FontWeight.Bold,
            maxLines = 1,
            softWrap = false,
            style = TextStyle(
                brush = Brush.radialGradient(
                    colors = SPOTLIGHT_COLORS,
                    center = Offset(x = half + swing * half, y = labelSize.height / 2f),
                    radius = (labelSize.height * BEAM_RADIUS).coerceAtLeast(1f)
                )
            )
        )
    }
}
